import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import GameCard from '@/components/feature/GameCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { games, categories } from '@/mocks/games';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'rpg');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredGames = useMemo(() => {
    return games.filter((g) => g.category === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.ceil(filteredGames.length / pageSize);
  const paginatedGames = filteredGames.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    setCurrentPage(1);
    window.history.replaceState(null, '', `/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
          <p className="text-foreground-500 text-lg">分类不存在</p>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCategory.name },
  ];

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 黄游免费下载`,
    description: currentCategory.description,
    url: `https://huangyou-free-download.com/category/${activeCategory}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: paginatedGames.map((game, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'VideoGame',
          name: game.title,
          url: `https://huangyou-free-download.com/article/${game.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                {currentCategory.name}
                <span className="text-foreground-400 text-xl ml-3 font-normal">黄游免费下载</span>
              </h1>
              <p className="text-foreground-500 text-base">{currentCategory.description}</p>
            </div>

            {/* Category Tabs */}
            <div className="mb-10 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max px-1 py-1 bg-background-100 rounded-full inline-flex">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50 shadow-sm'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                    <span className={`text-xs ${activeCategory === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Games Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10" data-product-shop>
              {paginatedGames.map((game) => (
                <GameCard key={game.id} {...game} />
              ))}
            </div>

            {filteredGames.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-gamepad-line text-foreground-300 text-3xl"></i>
                </div>
                <p className="text-foreground-500 text-lg">该分类下暂无游戏资源</p>
                <p className="text-foreground-400 text-sm mt-1">请稍后再来或浏览其他分类</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pb-16">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-lg border border-background-200 flex items-center justify-center text-foreground-500 hover:border-primary-300 hover:text-primary-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                      currentPage === i + 1
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-500'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-lg border border-background-200 flex items-center justify-center text-foreground-500 hover:border-primary-300 hover:text-primary-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}